import clsx from 'clsx';
import React, { forwardRef, useMemo, useState } from 'react';
import { useUniqID } from '../hooks';
import { Icon } from '../icon';
import { StarFillIcon } from '../icons/stars/star-fill-icon';
import { StarOutlineIcon } from '../icons/stars/start-outline-icon';
import styles from './styles.module.css';
import { checkRating, isInputChecked, strokeWidth } from './utils';
/**
 * `<Rate />` Компонент используется для сбора оценок по пятибалльной шкале.
 * В отличие от компонента `<Rating />` не только отображает, но и собирает оценки.
 *
 * @example ``` tsx
 *  <Rate onChange={() => {}} rating={3} />
 * ```
 */
export const Rate = /*#__PURE__*/forwardRef(function Rate({
  className,
  size = 'm',
  rating = 0,
  onChange,
  ...props
}, ref) {
  const color = 'brand-primary';
  const [hoverState, setHoverState] = useState(null);
  const validRating = useMemo(() => checkRating(rating), [rating]);
  const starsBarClassname = useMemo(() => clsx(styles.starsWrap, styles[size]), [size]);
  const rateUniqName = useUniqID();
  const drawStar = item => (/*#__PURE__*/React.createElement(React.Fragment, {
    key: item
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    value: item,
    id: `${rateUniqName}${item}`,
    name: rateUniqName,
    className: styles.inputRate,
    checked: isInputChecked(item, validRating),
    onChange: () => onChange(item)
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: `${rateUniqName}${item}`,
    className: styles.starLabel,
    "data-testid": `clickZone_${item}`,
    onMouseEnter: () => setHoverState(item)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.hiddenElement
  }, item, " stars"), /*#__PURE__*/React.createElement(Icon, {
    className: styles.starIcon,
    size: starSize[size],
    color: item <= (hoverState != null ? hoverState : validRating) ? color : 'control-secondary',
    "data-testid": `rateStar_${item}`
  }, item <= rating ? (/*#__PURE__*/React.createElement(StarFillIcon, null)) : (/*#__PURE__*/React.createElement(StarOutlineIcon, {
    strokeWidth: strokeWidth(item, validRating)
  }))))));
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    onMouseLeave: () => setHoverState(null),
    ref: ref,
    ...props
  }, /*#__PURE__*/React.createElement("input", {
    value: "0",
    id: `${rateUniqName}0`,
    type: "radio",
    name: rateUniqName,
    className: styles.inputRate,
    checked: isInputChecked(0, validRating),
    onChange: () => onChange(0)
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: `${rateUniqName}0`
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.hiddenElement
  }, "0 stars")), /*#__PURE__*/React.createElement("div", {
    className: starsBarClassname
  }, [...Array(5).keys()].map(item => drawStar(item + 1))));
});
const starSize = {
  s: 24,
  m: 32,
  l: 40,
  xl: 48
};